// Build status inside the standard ChatPanel (frozen editing → ready / stop / paused). Updates in
// place from the buildMode store; renders nothing outside a build.
import React, { useEffect, useRef, useState } from "react";
import { useThread } from "@assistant-ui/react";
import i18n from "../../../locales/i18n";
import { setBuildNotice, useBuildNotice, usePauseFrozen } from "./buildMode";

// Pause-card error line (also the chat composer's Improve reply): 429 = retrying right away cannot help,
// 402 = insufficient balance checked up front.
export const pauseErrorKey = (e) =>
  e && e.status === 429 ? "pauseBusy" : e && e.status === 402 ? "pauseNoCredits" : "pauseFailed";

// R8 pause after scene 1: stays until the user acts (a chat message does not clear it). Improve opens a note in place.
function PauseCard({ notice }) {
  const t = (k) => i18n.t(`aideaChat.buildNotice.${k}`);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [localBusy, setBusy] = useState(false);
  const frozen = usePauseFrozen(); // a chat confirm card's request in flight freezes this card too
  const busy = localBusy || frozen;
  const [failed, setFailed] = useState(null); // locale key of the error line
  const inFlight = useRef(false); // one click = one POST, even before `busy` re-renders
  const run = async (fn) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailed(null);
    try {
      await fn();
    } catch (e) {
      inFlight.current = false; // success keeps the card locked until the remount on the new job
      setFailed(pauseErrorKey(e));
      setBusy(false);
    }
  };
  return (
    <div className="aidea-build-notice" role="status">
      <div>{notice.title}</div>
      {notice.line && <div className="aidea-build-notice-line">{notice.line}</div>}
      {noteOpen && (
        <textarea
          className="aidea-build-notice-note"
          aria-label={t("improvePlaceholder")}
          placeholder={t("improvePlaceholder")}
          maxLength={1000}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      )}
      {failed && (
        <div role="alert" className="aidea-build-notice-error">
          {t(failed)}
        </div>
      )}
      <div className="aidea-build-notice-actions">
        {noteOpen ? (
          <>
            <button
              type="button"
              className="aidea-build-btn"
              disabled={busy || !note.trim()}
              onClick={() => run(() => notice.onImprove(note.trim()))}
            >
              {t("send")}
            </button>
            <button
              type="button"
              className="aidea-build-btn aidea-build-btn-ghost"
              disabled={busy}
              onClick={() => setNoteOpen(false)}
            >
              {t("cancel")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="aidea-build-btn"
              disabled={busy}
              onClick={() => run(notice.onContinue)}
            >
              {t("continue")}
            </button>
            <button
              type="button"
              className="aidea-build-btn aidea-build-btn-ghost"
              disabled={busy}
              onClick={() => setNoteOpen(true)}
            >
              {t("improve")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function BuildNotice() {
  const notice = useBuildNotice();
  const userMsgs = useThread((t) => t.messages.filter((m) => m.role === "user").length);
  const base = useRef(userMsgs);
  // A stop notice stays until the user's next message, then the chat is normal again.
  useEffect(() => {
    if (!notice) base.current = userMsgs;
    else if (notice.stop && userMsgs > base.current) setBuildNotice(null);
  }, [notice, userMsgs]);
  if (!notice) return null;
  if (notice.pause) return <PauseCard notice={notice} />;
  return (
    <div className="aidea-build-notice" role="status">
      <div>{notice.title}</div>
      {notice.line && <div className="aidea-build-notice-line">{notice.line}</div>}
    </div>
  );
}
